import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import copy from "./copy.generated.json";

export type Lang = "th" | "en";
export type I18nKey = keyof typeof copy;
export type Vars = Record<string, string | number>;

const STORAGE_KEY = "wc.lang";
const table = copy as unknown as Record<string, readonly [string, string]>;

export function hasKey(key: string): key is I18nKey {
  return key in table;
}

/** แปลข้อความ แทนที่ {name} ด้วยค่าใน vars ถ้าไม่พบ key จะคืน key เอง */
export function translate(lang: Lang, key: I18nKey, vars?: Vars): string {
  const row = table[key];
  let s = row ? row[lang === "en" ? 1 : 0] : key;
  if (vars) for (const k of Object.keys(vars)) s = s.split(`{${k}}`).join(String(vars[k]));
  return s;
}

export function readStoredLang(): Lang {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v === "th" || v === "en") return v;
  } catch {
    /* storage ไม่พร้อมใช้งาน */
  }
  return "th";
}

export type TFunction = (key: I18nKey, vars?: Vars) => string;

interface I18nValue {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: TFunction;
}

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ children, initialLang }: { children: ReactNode; initialLang?: Lang }) {
  const [lang, setLangState] = useState<Lang>(() => initialLang ?? readStoredLang());

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    try {
      window.localStorage.setItem(STORAGE_KEY, l);
    } catch {
      /* ignore */
    }
  }, []);

  const value = useMemo<I18nValue>(() => ({ lang, setLang, t: (key, vars) => translate(lang, key, vars) }), [lang, setLang]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside <I18nProvider>");
  return ctx;
}
